import type { MedplumClient } from "@medplum/core";
import type { CodeableConcept, Resource } from "@medplum/fhirtypes";
import {
  type CreateResourceIfNoneExistParams,
  type CreateResourceOptions,
  FhirResource,
  type UpsertResourceParams,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData } from "@vibrant/models/fhir/fhir-resource-types";
import { createLogger } from "@/lib/logger";
import { time } from "@/lib/time";
import { getVibrantSuperOrgId } from "@/lib/utils";
import { Source } from "@/models/source/Source";

const logger = createLogger("models/base/define-model");

/**
 * Provenance config for models whose sources are always DocumentReference-type.
 * Use SurveyProvenanceConfig instead if the model can have QuestionnaireResponse sources.
 */
export interface ProvenanceConfig<TData> {
  /** Extract FHIR source references from the create data. */
  sources: (data: TData) => Array<{ reference: string }>;
  /** Optional FHIR activity CodeableConcept. */
  activity?: (data: TData) => CodeableConcept | undefined;
  /** Optional ISO date string for when the source activity occurred. */
  occurredDate?: (data: TData) => string | undefined;
}

/**
 * Provenance config for models that may produce QuestionnaireResponse sources
 * (i.e., self-reported / survey data). Requires patientId so the factory can
 * set author correctly on the Source record. Use ProvenanceConfig for models
 * that only ever have DocumentReference sources.
 */
export interface SurveyProvenanceConfig<TData> extends ProvenanceConfig<TData> {
  patientId: (data: TData) => string;
}

export interface ModelConfig<
  TResource extends Resource,
  TData,
  TIdentifiers extends Record<string, string> = Record<string, string>,
  TExtensions extends Record<string, string> = Record<string, string>,
> {
  resourceType: TResource["resourceType"];
  identifiers?: TIdentifiers;
  extensions?: TExtensions;
  compartments?: ReadonlyArray<"practiceOrganization">;
  /** When present, Source.create is called automatically after every successful create(). */
  provenance?: ProvenanceConfig<TData> | SurveyProvenanceConfig<TData>;
  toFhirCreate: (data: TData) => ResourceData<TResource>;
  /**
   * Maps update data and the current resource state to a new full FHIR payload.
   * Required if the generated unsafeUpdate() method is used. Providing the existing
   * resource allows the function to merge/preserve fields.
   *
   * Concrete classes should implement their own `update()` with a domain-appropriate
   * signature (e.g., patch-based, field-level merging). See unsafeUpdate() for why
   * the factory-generated method is deliberately not named `update`.
   */
  toFhirUpdate?: (data: TData, existing: TResource) => ResourceData<TResource>;
}

/**
 * Factory that produces a typed FhirResource subclass from a config object.
 *
 * The generated class sets ResourceType and Identifier/Extension constants from config,
 * enforces required compartments at create time, and optionally writes provenance
 * (Source/Provenance) records automatically after the main resource write.
 *
 * Usage:
 *   const AllergyBase = defineFhirModel({ resourceType: "AllergyIntolerance", ... });
 *   export class Allergy extends AllergyBase { ... }
 */
export function defineFhirModel<
  TResource extends Resource,
  TData,
  TIdentifiers extends Record<string, string> = Record<string, string>,
  TExtensions extends Record<string, string> = Record<string, string>,
>(config: ModelConfig<TResource, TData, TIdentifiers, TExtensions>) {
  const {
    resourceType,
    identifiers,
    extensions,
    compartments,
    provenance,
    toFhirCreate,
    toFhirUpdate,
  } = config;

  // ---------------------------------------------------------------------------
  // Closure-scoped helpers — kept outside the class to avoid the
  // DefinedModel-vs-this binding issue and to centralise repeated logic.
  // ---------------------------------------------------------------------------

  /** Throws if a required compartment is missing from options. */
  function assertCompartments(method: string, options?: CreateResourceOptions): void {
    if (compartments?.includes("practiceOrganization") && !options?.practiceOrganization) {
      throw new Error(
        `[${resourceType}] ${method} requires options.practiceOrganization — ` +
          `this model declares "practiceOrganization" as a required compartment`,
      );
    }
  }

  /**
   * Wraps a raw FHIR resource in a model instance, preserving the subclass constructor.
   * Centralises the single `unknown`-cast so it appears exactly once.
   */
  function instantiate<T extends abstract new (...args: any) => any>(
    ctor: T,
    resource: TResource,
  ): InstanceType<T> {
    return new (ctor as unknown as new (resource: TResource) => InstanceType<T>)(resource);
  }

  /** Args accepted by the low-level provenance writer (resolved values, not extractors). */
  interface ResolvedProvenanceArgs {
    sources: Array<{ reference: string }>;
    /** Required when any source is a QuestionnaireResponse — used as Source.author. */
    patientId?: string;
    activity?: CodeableConcept;
    /** Empty/whitespace strings are coerced to undefined so we never emit invalid FHIR dateTime. */
    occurredDate?: string;
  }

  interface WriteSourceRecordParams {
    resourceId: string;
    args: ResolvedProvenanceArgs;
    options: CreateResourceOptions | undefined;
  }

  /**
   * Writes a Source (provenance) record from already-resolved args. Shared by both the
   * config-extractor path (writeProvenanceRecord) and the direct-args path
   * (ensureProvenanceFromRefs) so the author/assembler heuristic and FHIR-dateTime
   * coercion live in exactly one place.
   *
   * Failure is logged but never thrown — provenance never rolls back a resource write.
   *
   * NOTE: assembler defaults to `getVibrantSuperOrgId()`. This factory is
   * app-specific infrastructure, not a general-purpose library primitive.
   */
  async function writeSourceRecord(
    medplum: MedplumClient,
    { resourceId, args, options }: WriteSourceRecordParams,
  ): Promise<void> {
    try {
      if (args.sources.length === 0) {
        logger.debug(`[${resourceType}] no provenance sources, skipping`, { id: resourceId });
        return;
      }

      const hasSurveyResponse = args.sources.some((s) =>
        s.reference.startsWith("QuestionnaireResponse/"),
      );
      // QuestionnaireResponse sources imply patient-authored flow. Without a patientId we
      // can't emit a Source.author and must fall back to assembler — Source.create requires
      // at least one. Surface the misconfiguration loudly so it's caught in dev.
      if (hasSurveyResponse && !args.patientId) {
        logger.warn(
          `[${resourceType}] QuestionnaireResponse source present but patientId is empty — ` +
            `falling back to assembler`,
          { id: resourceId },
        );
      }

      const hasPatientAuthor = Boolean(hasSurveyResponse && args.patientId);

      // Coerce empty/whitespace strings to undefined and forward the trimmed value so
      // callers don't have to litter every config with `data.field || undefined`. Empty
      // or whitespace-only strings are not valid FHIR dateTimes; omitting the field is
      // the correct semantics when no date is known.
      const occurredDate = args.occurredDate?.trim() || undefined;

      await Source.create(
        medplum,
        {
          for: { reference: `${resourceType}/${resourceId}` },
          date: time().toISOString(),
          author: hasPatientAuthor ? { reference: `Patient/${args.patientId}` } : undefined,
          assembler: hasPatientAuthor
            ? undefined
            : { reference: `Organization/${getVibrantSuperOrgId()}` },
          sources: args.sources,
          activity: args.activity,
          occurredDate,
        },
        options,
      );
    } catch (error) {
      logger.error(`[${resourceType}] failed to write provenance`, {
        id: resourceId,
        error,
      });
    }
  }

  interface WriteProvenanceRecordParams {
    resourceId: string;
    data: TData;
    options: CreateResourceOptions | undefined;
    provenanceConfig: ProvenanceConfig<TData> | SurveyProvenanceConfig<TData>;
  }

  /**
   * Resolves a ProvenanceConfig against TData and forwards to writeSourceRecord.
   *
   * Provenance is written **per write operation**: every successful create(), upsert(),
   * or unsafeUpdate() that produces non-empty sources writes a new Source record. This
   * means upsert() may create multiple provenance records if called repeatedly with the
   * same data — callers are responsible for deduplication if that matters.
   */
  async function writeProvenanceRecord(
    medplum: MedplumClient,
    { resourceId, data, options, provenanceConfig }: WriteProvenanceRecordParams,
  ): Promise<void> {
    // Extractors run inside writeSourceRecord's try/catch so any throw — from sources(),
    // patientId(), activity(), occurredDate() — is logged rather than propagated. The
    // parent resource is already committed by the time we get here, so letting an
    // extractor throw would make the overall operation look failed and risk duplicate
    // resources on caller retry.
    try {
      const sources = provenanceConfig.sources(data);
      const isSurveyConfig = "patientId" in provenanceConfig;
      const hasSurveyResponse = sources.some((s) =>
        s.reference.startsWith("QuestionnaireResponse/"),
      );

      // Misconfiguration: QuestionnaireResponse source but model used plain ProvenanceConfig
      // (no patientId extractor). The patientId-empty case is handled inside writeSourceRecord.
      if (hasSurveyResponse && !isSurveyConfig) {
        logger.warn(
          `[${resourceType}] QuestionnaireResponse source present but model uses plain ` +
            `ProvenanceConfig (no patientId) — falling back to assembler`,
          { id: resourceId },
        );
      }

      await writeSourceRecord(medplum, {
        resourceId,
        args: {
          sources,
          patientId: isSurveyConfig ? provenanceConfig.patientId(data) : undefined,
          activity: provenanceConfig.activity?.(data),
          occurredDate: provenanceConfig.occurredDate?.(data),
        },
        options,
      });
    } catch (error) {
      logger.error(`[${resourceType}] failed to write provenance`, {
        id: resourceId,
        error,
      });
    }
  }

  interface CreateIfNoneExistParams {
    data: TData;
    ifNoneExistQuery: string;
    options?: CreateResourceOptions;
  }

  interface UpsertParams {
    data: TData;
    query: Record<string, unknown>;
    options?: CreateResourceOptions;
  }

  interface UnsafeUpdateParams {
    id: string;
    data: TData;
    options?: CreateResourceOptions;
  }

  interface EnsureProvenanceParams {
    resourceId: string;
    data: TData;
    options?: CreateResourceOptions;
  }

  interface EnsureProvenanceFromRefsParams {
    resourceId: string;
    args: ResolvedProvenanceArgs;
    options?: CreateResourceOptions;
  }

  // ---------------------------------------------------------------------------

  class DefinedModel extends FhirResource<TResource> {
    static readonly ResourceType = resourceType;
    static readonly Identifier = (identifiers ?? {}) as TIdentifiers;
    static readonly Extension = (extensions ?? {}) as TExtensions;

    /**
     * Creates a resource in three phases:
     *   1. Compartment guard — throws if required compartment missing from options
     *   2. Resource write   — toFhirCreate(data) → FhirResource.createResource (applies compartments)
     *   3. Provenance write — Source.create if provenance config present and sources non-empty
     *
     * Provenance failure is logged but never throws — it does not roll back the resource write.
     */
    // biome-ignore lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
    static async create<T extends typeof DefinedModel>(
      this: T,
      medplum: MedplumClient,
      data: TData,
      options?: CreateResourceOptions,
    ): Promise<InstanceType<T>> {
      assertCompartments("create()", options);

      // `this as unknown as { createResource(...) }` — TypeScript cannot call a protected
      // static method via a generic `this: T`, so we cast to an inline interface that
      // preserves the TResource binding.
      const fhirData = toFhirCreate(data);
      const resource = await (
        this as unknown as {
          createResource(
            medplum: MedplumClient,
            data: ResourceData<TResource>,
            options?: CreateResourceOptions,
          ): Promise<TResource>;
        }
      ).createResource(medplum, fhirData, options);

      if (provenance && resource.id) {
        await writeProvenanceRecord(medplum, {
          resourceId: resource.id,
          data,
          options,
          provenanceConfig: provenance,
        });
      }

      return instantiate(this, resource);
    }

    /**
     * Same as create(), except Medplum only writes when no existing resource matches
     * `ifNoneExistQuery`. The match runs server-side inside the write, so two callers
     * that both read "nothing exists" cannot both insert a row.
     *
     * Use this wherever the caller's own read-then-create is the dedupe (one vital per
     * member per LOINC, one record per document). A plain create() there produces a
     * duplicate whenever two parses of the same member overlap.
     *
     * The query is a FHIR search string, e.g. `patient=Patient/123&code=http://loinc.org|8302-2`.
     * Medplum returns the existing resource on a single match and rejects the write with
     * 412 when the query matches more than one — keep the query narrow.
     *
     * Provenance failure is logged but never throws, same as create().
     */
    static async createIfNoneExist<T extends typeof DefinedModel>(
      this: T,
      medplum: MedplumClient,
      { data, ifNoneExistQuery, options }: CreateIfNoneExistParams,
    ): Promise<InstanceType<T>> {
      assertCompartments("createIfNoneExist()", options);

      const fhirData = toFhirCreate(data);
      const resource = await (
        this as unknown as {
          createResourceIfNoneExist(
            medplum: MedplumClient,
            params: CreateResourceIfNoneExistParams<ResourceData<TResource>>,
          ): Promise<TResource>;
        }
      ).createResourceIfNoneExist(medplum, { resource: fhirData, ifNoneExistQuery, options });

      if (provenance && resource.id) {
        await writeProvenanceRecord(medplum, {
          resourceId: resource.id,
          data,
          options,
          provenanceConfig: provenance,
        });
      }

      return instantiate(this, resource);
    }

    /**
     * Upserts a resource in three phases:
     *   1. Compartment guard — throws if required compartment missing from options
     *   2. Resource write   — toFhirCreate(data) → FhirResource.upsertResource (creates or patches)
     *   3. Provenance write — Source.create if provenance config present and sources non-empty
     *
     * Provenance is written per-write-operation: each upsert() writes a new Source record
     * if sources are non-empty, even if the underlying resource already existed. Callers
     * are responsible for deduplication if duplicate provenance records are undesirable.
     *
     * Provenance failure is logged but never throws.
     */
    static async upsert<T extends typeof DefinedModel>(
      this: T,
      medplum: MedplumClient,
      { data, query, options }: UpsertParams,
    ): Promise<InstanceType<T>> {
      assertCompartments("upsert()", options);

      const fhirData = toFhirCreate(data);
      const resource = await (
        this as unknown as {
          upsertResource(
            medplum: MedplumClient,
            params: UpsertResourceParams<ResourceData<TResource>>,
          ): Promise<TResource & { id: string }>;
        }
      ).upsertResource(medplum, { data: fhirData, query, options });

      if (provenance && resource.id) {
        await writeProvenanceRecord(medplum, {
          resourceId: resource.id,
          data,
          options,
          provenanceConfig: provenance,
        });
      }

      return instantiate(this, resource);
    }

    /**
     * Low-level full-resource replacement. Named "unsafe" because it bypasses
     * FhirResource.createResource and calls medplum.updateResource directly —
     * compartment tagging and other base-class write infrastructure are NOT applied.
     * The caller (or toFhirUpdate) must preserve compartment membership from the
     * existing resource.
     *
     * **Concrete classes should implement their own `update()` method** with a
     * domain-appropriate signature (e.g., patch-based field merging via
     * `patchResource`, or a typed data shape). The factory deliberately does not
     * generate an `update()` so that every model is forced to choose the right
     * update semantics for its resource type.
     *
     * Pipeline:
     *   1. Compartment guard — throws if required compartment missing from options
     *   2. Resource read    — captures existing state + meta.versionId (future ETag support)
     *   3. Resource write   — toFhirUpdate(data, existing) → medplum.updateResource
     *   4. Provenance write — Source.create if configured and sources non-empty
     *
     * Throws immediately if toFhirUpdate is not configured in the ModelConfig.
     */
    static async unsafeUpdate<T extends typeof DefinedModel>(
      this: T,
      medplum: MedplumClient,
      { id, data, options }: UnsafeUpdateParams,
    ): Promise<InstanceType<T>> {
      if (!toFhirUpdate) {
        throw new Error(`[${resourceType}] unsafeUpdate() requires toFhirUpdate in ModelConfig`);
      }

      assertCompartments("unsafeUpdate()", options);

      // Reading first also captures meta.versionId for future ETag support.
      const existing = await medplum.readResource(resourceType, id);
      // `existing as TResource` — readResource returns the Resource union type; the cast
      // is safe because resourceType is constrained to TResource["resourceType"] in ModelConfig.
      const fhirData = toFhirUpdate(data, existing as TResource);
      const resource = await medplum.updateResource({
        ...fhirData,
        resourceType,
        id,
      } as TResource);

      if (provenance && resource.id) {
        await writeProvenanceRecord(medplum, {
          resourceId: resource.id,
          data,
          options,
          provenanceConfig: provenance,
        });
      }

      return instantiate(this, resource);
    }

    /**
     * Writes a provenance record for an existing resource using the model's
     * configured ProvenanceConfig / SurveyProvenanceConfig. Use when the main
     * resource write happened outside the factory's create/upsert/unsafeUpdate
     * pipeline (e.g., after a patch or merge) but you still want the factory's
     * centralized provenance semantics (author/assembler heuristic, error
     * swallowing + logging, etc.) to apply.
     *
     * Throws if the model was defined without a provenance config.
     */
    static async ensureProvenance(
      medplum: MedplumClient,
      { resourceId, data, options }: EnsureProvenanceParams,
    ): Promise<void> {
      if (!provenance) {
        throw new Error(
          `[${resourceType}] ensureProvenance() requires a provenance config in ModelConfig`,
        );
      }
      await writeProvenanceRecord(medplum, {
        resourceId,
        data,
        options,
        provenanceConfig: provenance,
      });
    }

    /**
     * Writes a provenance record from already-resolved args, bypassing the
     * model's TData extractors. Use from `update()` / patch flows that don't
     * have a full TData on hand and would otherwise need to fabricate a
     * synthetic create-request just to satisfy the extractors.
     *
     * Shares the same author/assembler heuristic and empty-dateTime coercion
     * as the config-driven path; failure is logged, never thrown.
     */
    static async ensureProvenanceFromRefs(
      medplum: MedplumClient,
      { resourceId, args, options }: EnsureProvenanceFromRefsParams,
    ): Promise<void> {
      await writeSourceRecord(medplum, { resourceId, args, options });
    }

    /**
     * Reads a resource by its Medplum ID and wraps it in a model instance.
     * Uses medplum.readResource directly (no compartment filtering needed for reads).
     */
    static async getById<T extends typeof DefinedModel>(
      this: T,
      medplum: MedplumClient,
      id: string,
    ): Promise<InstanceType<T>> {
      const resource = await medplum.readResource(resourceType, id);
      return instantiate(this, resource as TResource);
    }
  }

  return DefinedModel;
}
